import React, { useState, useEffect } from "react";
import axios from "axios";
import { FileBarChart2, Download, Printer, TrendingUp, TrendingDown, DollarSign, Receipt, Users, Wallet, MapPin, Scale, Coins } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const monthOptions = (() => {
  const year = new Date().getFullYear();
  const names = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  const opts = names.map((label, idx) => ({ kode: `${year}-${String(idx + 1).padStart(2, "0")}`, label }));
  opts.push({ kode: `${year}`, label: `Semua Tahun Ini (${year})` });
  return opts;
})();

const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

const REPORTS = [
  { key: "laba-rugi", label: "Laporan Laba Rugi", icon: TrendingUp },
  { key: "neraca", label: "Laporan Neraca", icon: Scale },
  { key: "perubahan-modal", label: "Laporan Perubahan Modal", icon: Coins },
  { key: "pajak", label: "Laporan Pajak", icon: Receipt },
  { key: "bagi-hasil", label: "Laporan Bagi Hasil / Sharing Mitra", icon: Users },
];

export const LaporanPage = () => {
  const currentKode = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const [reportType, setReportType] = useState("laba-rugi");
  const [periode, setPeriode] = useState(currentKode);
  const [wilayahFilter, setWilayahFilter] = useState("ALL");
  const [desaFilter, setDesaFilter] = useState("ALL");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [neraca, setNeraca] = useState(null);
  const [loadingNeraca, setLoadingNeraca] = useState(false);
  const [pmodal, setPmodal] = useState(null);
  const [loadingPmodal, setLoadingPmodal] = useState(false);
  const [tren, setTren] = useState(null);
  const [brand, setBrand] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ periode });
        if (wilayahFilter && wilayahFilter !== "ALL") params.append("wilayah_id", wilayahFilter);
        if (desaFilter && desaFilter !== "ALL") params.append("desa", desaFilter);
        const res = await axios.get(`${BACKEND_URL}/api/laporan/keuangan?${params.toString()}`, { withCredentials: true });
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [periode, wilayahFilter, desaFilter]);

  useEffect(() => {
    if (reportType !== "neraca") return;
    const fetchNeraca = async () => {
      setLoadingNeraca(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/laporan/neraca?periode=${periode}`, { withCredentials: true });
        setNeraca(res.data);
      } catch (e) { console.error(e); }
      finally { setLoadingNeraca(false); }
    };
    fetchNeraca();
  }, [reportType, periode]);

  useEffect(() => {
    if (reportType !== "perubahan-modal") return;
    const fetchPmodal = async () => {
      setLoadingPmodal(true);
      try {
        const [res, trenRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/laporan/perubahan-modal?periode=${periode}`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/laporan/tren-modal?periode=${periode}&bulan=6`, { withCredentials: true }).catch(() => ({ data: { rows: [] } })),
        ]);
        setPmodal(res.data);
        setTren(trenRes.data);
      } catch (e) { console.error(e); }
      finally { setLoadingPmodal(false); }
    };
    fetchPmodal();
  }, [reportType, periode]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/branding`, { withCredentials: true }).then((r) => setBrand(r.data || {})).catch(() => {});
  }, []);

  const wilayahOptions = data?.wilayah_options || [];
  const desaOptions = wilayahFilter !== "ALL"
    ? [...new Set(wilayahOptions.filter((w) => w.id === wilayahFilter).map((w) => w.desa).filter(Boolean))]
    : (data?.desa_options || []);
  const scopeLocked = data?.scope_locked;

  const periodeLabel = monthOptions.find((o) => o.kode === periode)?.label || periode;
  const activeReport = REPORTS.find((r) => r.key === reportType);

  const printLetterhead = (title, bodyHtml) => {
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const perus = brand.perusahaan || {};
    const namaPerus = perus.nama_isp || brand.app_name || "PERUSAHAAN";
    const logo = brand.app_logo || "";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
    <style>
      *{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px}
      .head{display:flex;align-items:center;gap:14px;border-bottom:3px double #111;padding-bottom:10px;margin-bottom:6px}
      .head img{height:54px;width:auto}.head h1{font-size:18px;margin:0}.head p{font-size:11px;margin:2px 0 0;color:#444}
      .title{text-align:center;margin:10px 0 14px}.title h2{font-size:15px;margin:0;text-transform:uppercase;letter-spacing:.5px}
      .title p{font-size:11px;color:#555;margin:2px 0 0}
      table{width:100%;border-collapse:collapse;font-size:11.5px;margin:8px 0}
      th,td{border:1px solid #333;padding:6px 8px}th{background:#eee;text-align:left;font-size:10px;text-transform:uppercase}
      td.r{text-align:right;font-family:'Courier New',monospace;white-space:nowrap}td.c{text-align:center}
      .b{font-weight:bold}tr.total td{background:#f4f4f4;font-weight:bold}
      h3.sec{font-size:12px;margin:16px 0 4px;border-left:4px solid #111;padding-left:8px}
      .neg{color:#b00}
      .foot{margin-top:26px;font-size:11px;display:flex;justify-content:flex-end}
      .sign{text-align:center;width:220px}.sign .line{margin-top:60px;border-top:1px solid #111;padding-top:4px}
      @media print{body{margin:12mm}}
    </style></head><body>
      <div class="head">${logo ? `<img src="${esc(logo)}" alt="logo"/>` : ""}
        <div><h1>${esc(namaPerus)}</h1>${perus.alamat ? `<p>${esc(perus.alamat)}</p>` : ""}${(perus.telepon || perus.whatsapp) ? `<p>Telp/WA: ${esc(perus.telepon || perus.whatsapp)}</p>` : ""}</div>
      </div>
      <div class="title"><h2>${esc(title)}</h2><p>Periode ${esc(periodeLabel)} · Dicetak ${new Date().toLocaleString("id-ID")}</p></div>
      ${bodyHtml}
      <div class="foot"><div class="sign"><div>Mengetahui / Pimpinan,</div><div class="line">( ............................... )</div></div></div>
      <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
    </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Popup diblokir browser. Izinkan popup untuk mencetak."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  const handlePrint = () => {
    const rpx = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
    if (reportType === "neraca" && neraca) {
      const rows = (arr) => arr.map((r) => `<tr><td>${r.label}</td><td class="r ${r.value < 0 ? "neg" : ""}">${rpx(r.value)}</td></tr>`).join("");
      const body = `
        <h3 class="sec">ASET</h3><table>${rows(neraca.aset_rows)}<tr class="total"><td>TOTAL ASET</td><td class="r">${rpx(neraca.total_aset)}</td></tr></table>
        <h3 class="sec">KEWAJIBAN</h3><table>${rows(neraca.kewajiban_rows)}<tr class="total"><td>TOTAL KEWAJIBAN</td><td class="r">${rpx(neraca.total_kewajiban)}</td></tr></table>
        <h3 class="sec">EKUITAS</h3><table>${rows(neraca.ekuitas_rows)}<tr class="total"><td>TOTAL EKUITAS</td><td class="r">${rpx(neraca.total_ekuitas)}</td></tr></table>
        <table><tr class="total"><td>TOTAL KEWAJIBAN + EKUITAS</td><td class="r">${rpx(neraca.total_kewajiban_ekuitas)}</td></tr></table>
        <p style="font-size:11px;margin-top:8px">Status: <b>${neraca.balanced ? "SEIMBANG" : "TIDAK SEIMBANG"}</b></p>`;
      printLetterhead("Laporan Neraca", body);
      return;
    }
    if (reportType === "perubahan-modal" && pmodal) {
      const ringkas = `<table>
        <tr><td>Modal Awal</td><td class="r">${rpx(pmodal.modal_awal)}</td></tr>
        <tr><td>+ Setoran Modal</td><td class="r">${rpx(pmodal.setoran_modal)}</td></tr>
        <tr><td>+ Laba Bersih</td><td class="r ${pmodal.laba_bersih < 0 ? "neg" : ""}">${rpx(pmodal.laba_bersih)}</td></tr>
        <tr><td>− Pengambilan Dividen / Prive</td><td class="r neg">${rpx(pmodal.prive)}</td></tr>
        <tr class="total"><td>MODAL AKHIR</td><td class="r">${rpx(pmodal.modal_akhir)}</td></tr></table>`;
      const rinc = `<h3 class="sec">Rincian Modal per Mitra / Owner</h3><table>
        <thead><tr><th>Nama</th><th>Tipe</th><th style="text-align:right">Modal Disetor</th><th style="text-align:right">Prive</th><th style="text-align:right">Modal Bersih</th><th style="text-align:center">% Sharing</th></tr></thead>
        <tbody>${pmodal.rincian_mitra.map((r) => `<tr><td>${r.nama}</td><td>${r.tipe}</td><td class="r">${rpx(r.modal_disetor)}</td><td class="r neg">${rpx(r.prive)}</td><td class="r b">${rpx(r.modal_bersih)}</td><td class="c">${r.prosentase_sharing == null ? "-" : Number(r.prosentase_sharing).toFixed(1) + "%"}</td></tr>`).join("")}
        <tr class="total"><td colspan="2">TOTAL</td><td class="r">${rpx(pmodal.total_modal_disetor)}</td><td class="r">${rpx(pmodal.total_prive)}</td><td class="r">${rpx(pmodal.total_modal_bersih)}</td><td></td></tr></tbody></table>`;
      printLetterhead("Laporan Perubahan Modal", ringkas + rinc);
      return;
    }
    if (!data) { alert("Data laporan belum siap. Coba lagi sesaat."); return; }
    if (reportType === "laba-rugi") {
      const body = `<table><tbody>${(data.laba_rugi_rows || []).map((r) => {
        const isBold = ["total", "subtotal", "final"].includes(r.tipe);
        const prefix = (r.tipe === "minus" || r.tipe === "alokasi") ? "( − ) " : "";
        return `<tr class="${isBold ? "total" : ""}"><td class="${isBold ? "b" : ""}">${prefix}${r.label}</td><td class="r ${r.value < 0 ? "neg" : ""}">${rpx(r.value)}</td></tr>`;
      }).join("")}</tbody></table>
      <table style="margin-top:10px"><tbody>
        <tr><td>Total Pendapatan</td><td class="r">${rpx(data.total_pendapatan)}</td></tr>
        <tr><td>Laba Bersih</td><td class="r ${data.laba_bersih < 0 ? "neg" : ""}">${rpx(data.laba_bersih)}</td></tr>
        <tr class="total"><td class="b">Sisa Profit Siap Diambil</td><td class="r">${rpx(data.sisa_profit)}</td></tr>
      </tbody></table>`;
      printLetterhead("Laporan Laba Rugi", body);
      return;
    }
    if (reportType === "pajak") {
      const body = `<table>
        <thead><tr><th>Judul</th><th>Jenis</th><th style="text-align:right">Dasar Pengenaan</th><th style="text-align:center">Tarif</th><th style="text-align:right">Nominal</th><th style="text-align:center">Status</th></tr></thead>
        <tbody>${(data.pajak_detail || []).map((p) => `<tr><td>${p.judul || "-"}</td><td>${p.jenis || "-"}</td><td class="r">${rpx(p.dasar_pengenaan)}</td><td class="c">${p.tarif_persen ?? 0}%</td><td class="r">${rpx(p.nominal)}</td><td class="c">${p.status === "SUDAH_SETOR" ? "Sudah Setor" : "Belum Setor"}</td></tr>`).join("") || '<tr><td colspan="6" style="text-align:center">Tidak ada data pajak pada periode ini</td></tr>'}</tbody></table>`;
      printLetterhead("Laporan Pajak", body);
      return;
    }
    if (reportType === "bagi-hasil") {
      const body = `<table>
        <thead><tr><th>Mitra / Investor</th><th style="text-align:right">Modal</th><th style="text-align:center">% Sharing</th><th style="text-align:center">Jml Pelanggan</th><th style="text-align:right">Pendapatan Terkait</th><th style="text-align:right">Bagian Laba</th></tr></thead>
        <tbody>${(data.mitra_sharing || []).map((m) => `<tr><td>${m.nama}</td><td class="r">${rpx(m.jumlah_modal)}</td><td class="c">${Number(m.prosentase_sharing).toFixed(1)}%</td><td class="c">${m.jumlah_pelanggan}</td><td class="r">${rpx(m.pendapatan_terkait)}</td><td class="r b">${rpx(m.bagian)}</td></tr>`).join("") || '<tr><td colspan="6" style="text-align:center">Belum ada data bagi hasil</td></tr>'}
        <tr class="total"><td class="b">TOTAL BAGIAN MITRA</td><td colspan="4"></td><td class="r">${rpx((data.mitra_sharing || []).reduce((s, m) => s + (m.bagian || 0), 0))}</td></tr></tbody></table>
        <p style="font-size:11px;margin-top:8px">Sisa Profit Perusahaan: <b>${rpx(data.sisa_profit)}</b> · Bagian Perusahaan: <b>${rpx(data.bagian_perusahaan)}</b></p>`;
      printLetterhead("Laporan Bagi Hasil / Sharing Mitra", body);
      return;
    }
    window.print();
  };

  const handleExportCSV = () => {
    if (reportType === "perubahan-modal") {
      if (!pmodal) return;
      const rows = [
        ["LAPORAN PERUBAHAN MODAL", `Periode ${periodeLabel}`],
        ["Modal Awal", pmodal.modal_awal],
        ["Setoran Modal", pmodal.setoran_modal],
        ["Laba Bersih", pmodal.laba_bersih],
        ["Pengambilan Dividen / Prive", -pmodal.prive],
        ["Modal Akhir", pmodal.modal_akhir],
        ["", ""],
        ["RINCIAN MODAL DISETOR", "Nominal", "% Sharing"],
        ...pmodal.rincian_mitra.map((r) => [r.nama, r.modal_disetor, r.prosentase_sharing ?? "-"]),
        ["TOTAL", pmodal.total_modal_disetor, ""],
      ];
      const csv = rows.map((r) => r.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Perubahan_Modal_${periode}.csv`; a.click();
      return;
    }
    if (reportType === "neraca") {
      if (!neraca) return;
      const rows = [
        ["NERACA", `Periode ${periodeLabel}`],
        ["ASET", ""],
        ...neraca.aset_rows.map((r) => [r.label, r.value]),
        ["TOTAL ASET", neraca.total_aset],
        ["KEWAJIBAN", ""],
        ...neraca.kewajiban_rows.map((r) => [r.label, r.value]),
        ["TOTAL KEWAJIBAN", neraca.total_kewajiban],
        ["EKUITAS", ""],
        ...neraca.ekuitas_rows.map((r) => [r.label, r.value]),
        ["TOTAL EKUITAS", neraca.total_ekuitas],
        ["TOTAL KEWAJIBAN + EKUITAS", neraca.total_kewajiban_ekuitas],
      ];
      const csv = rows.map((r) => r.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Neraca_${periode}.csv`; a.click();
      return;
    }
    if (!data) return;
    let rows = [];
    let filename = "Laporan";
    if (reportType === "laba-rugi") {
      filename = "Laba_Rugi";
      rows = [["Keterangan", "Nominal"], ...data.laba_rugi_rows.map((r) => [r.label, r.value])];
    } else if (reportType === "pajak") {
      filename = "Pajak";
      rows = [["Judul", "Jenis", "Dasar Pengenaan", "Tarif %", "Nominal", "Status"],
        ...data.pajak_detail.map((p) => [p.judul, p.jenis, p.dasar_pengenaan, p.tarif_persen, p.nominal, p.status])];
    } else {
      filename = "Bagi_Hasil_Mitra";
      rows = [["Mitra", "Modal", "% Sharing", "Jml Pelanggan", "Pendapatan Terkait", "Bagian Laba"],
        ...data.mitra_sharing.map((m) => [m.nama, m.jumlah_modal, m.prosentase_sharing, m.jumlah_pelanggan, m.pendapatan_terkait, m.bagian])];
    }
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${filename}_${periode}.csv`; a.click();
  };

  return (
    <div className="space-y-6" data-testid="laporan-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl no-print">
        <div>
          <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-[10px] font-bold uppercase tracking-wider">Financial Reports</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Laporan Keuangan</h1>
          <p className="text-xs text-slate-400 mt-0.5">Laba Bersih = Pendapatan − (Operasional + Pajak). Filter wilayah/desa mengalokasikan beban bersama secara proporsional.</p>
          {scopeLocked && <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[10px] font-bold" data-testid="scope-locked-badge"><MapPin className="w-3 h-3" /> Terbatas pada wilayah pelanggan sharing Anda</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={reportType} onChange={(e) => setReportType(e.target.value)} data-testid="report-type-select" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold min-w-[220px]">
            {(scopeLocked ? REPORTS.filter((r) => r.key !== "pajak" && r.key !== "neraca" && r.key !== "perubahan-modal") : REPORTS).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <select value={wilayahFilter} onChange={(e) => { setWilayahFilter(e.target.value); setDesaFilter("ALL"); }} data-testid="report-wilayah-select" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold">
            <option value="ALL">{scopeLocked ? "Semua Wilayah Saya" : "Semua Wilayah"}</option>
            {wilayahOptions.map((w) => <option key={w.id} value={w.id}>{w.nama_wilayah}{w.desa ? ` — ${w.desa}` : ""}</option>)}
          </select>
          <select value={desaFilter} onChange={(e) => setDesaFilter(e.target.value)} data-testid="report-desa-select" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold">
            <option value="ALL">Semua Desa</option>
            {desaOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={periode} onChange={(e) => setPeriode(e.target.value)} data-testid="report-periode-select" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold">
            {monthOptions.map((o) => <option key={o.kode} value={o.kode}>{o.label}</option>)}
          </select>
          <button onClick={handleExportCSV} data-testid="export-csv-btn" className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700 flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> CSV</button>
          <button onClick={handlePrint} data-testid="print-report-btn" className="px-3.5 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-lg shadow-purple-600/30"><Printer className="w-3.5 h-3.5" /> Cetak</button>
        </div>
      </div>

      {loading && <div className="p-10 text-center text-slate-500 text-sm">Memuat laporan…</div>}

      {!loading && data && (
        <div data-testid={`report-${reportType}`}>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {activeReport && <activeReport.icon className="w-5 h-5 text-purple-400" />}
            <h2 className="text-lg font-black text-white">{activeReport?.label}</h2>
            <span className="text-xs text-slate-400">— Periode {periodeLabel}</span>
            {data.scoped && reportType !== "neraca" && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30" data-testid="scoped-note">Terfilter wilayah · beban dialokasikan {Math.round((data.ratio || 0) * 100)}% proporsional</span>}
          </div>

          {reportType === "laba-rugi" && <LabaRugi data={data} />}
          {reportType === "neraca" && <Neraca data={neraca} loading={loadingNeraca} periodeLabel={periodeLabel} />}
          {reportType === "perubahan-modal" && <PerubahanModal data={pmodal} loading={loadingPmodal} periodeLabel={periodeLabel} tren={tren} />}
          {reportType === "pajak" && !scopeLocked && <Pajak data={data} />}
          {reportType === "bagi-hasil" && <BagiHasil data={data} />}
        </div>
      )}
    </div>
  );
};

const LabaRugi = ({ data }) => {
  const profit = data.laba_bersih >= 0;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-xs text-slate-400 font-semibold">Total Pendapatan</p><p className="text-2xl font-black text-emerald-400 mt-1 font-mono">{rp(data.total_pendapatan)}</p><span className="text-[11px] text-emerald-400 flex items-center gap-1 mt-1"><TrendingUp className="w-3 h-3" /> Tagihan + Pendapatan Lain</span></div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-xs text-slate-400 font-semibold">Pengurang Operasional</p><p className="text-2xl font-black text-rose-400 mt-1 font-mono">{rp(data.total_pengurang)}</p><span className="text-[11px] text-rose-400 flex items-center gap-1 mt-1"><TrendingDown className="w-3 h-3" /> Operasional + Pajak</span></div>
        <div className={`p-4 rounded-2xl border ${profit ? "bg-cyan-950/30 border-cyan-500/40" : "bg-rose-950/30 border-rose-500/40"}`}><p className="text-xs text-slate-400 font-semibold">Laba Bersih</p><p className={`text-2xl font-black mt-1 font-mono ${profit ? "text-cyan-400" : "text-rose-400"}`}>{rp(data.laba_bersih)}</p><span className={`text-[11px] flex items-center gap-1 mt-1 ${profit ? "text-cyan-400" : "text-rose-400"}`}><DollarSign className="w-3 h-3" /> {profit ? "Untung" : "Rugi"}</span></div>
        <div className="p-4 rounded-2xl bg-emerald-950/30 border border-emerald-500/40"><p className="text-xs text-slate-400 font-semibold">Sisa Profit (Prive)</p><p className="text-2xl font-black text-emerald-300 mt-1 font-mono">{rp(data.sisa_profit)}</p><span className="text-[11px] text-emerald-300 flex items-center gap-1 mt-1"><DollarSign className="w-3 h-3" /> Setelah cadangan {data.dana_cadangan_persen ?? 0}%</span></div>
      </div>

      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <h3 className="text-sm font-bold text-white border-b border-slate-800 pb-2.5 mb-2 flex items-center gap-2"><FileBarChart2 className="w-4 h-4 text-purple-400" /> Rincian Laba Rugi</h3>
        <table className="w-full text-left text-xs">
          <tbody>
            {data.laba_rugi_rows.map((r, idx) => {
              const isTotal = r.tipe === "total";
              const isSub = r.tipe === "subtotal";
              const isFinal = r.tipe === "final";
              const isAlokasi = r.tipe === "alokasi";
              const color = r.tipe === "minus" ? "text-rose-400"
                : r.tipe === "plus" ? "text-emerald-400"
                : isAlokasi ? "text-amber-400"
                : isFinal ? "text-emerald-300"
                : isTotal ? (data.laba_bersih >= 0 ? "text-cyan-400" : "text-rose-400")
                : "text-white";
              return (
                <tr key={idx} className={`${isTotal || isFinal ? "border-t-2 border-slate-700" : isSub ? "border-t border-slate-800" : "border-b border-slate-800/50"}`} data-testid={`lr-row-${idx}`}>
                  <td className={`py-2.5 px-3 ${isTotal || isSub || isFinal ? "font-black text-white" : "text-slate-300"}`}>{(r.tipe === "minus" || isAlokasi) && "( − ) "}{r.label}</td>
                  <td className={`py-2.5 px-3 text-right font-mono font-bold ${color} ${isTotal || isFinal ? "text-base" : ""}`}>{rp(r.value)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Pajak = ({ data }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-xs text-slate-400 font-semibold">Total Pajak Periode</p><p className="text-2xl font-black text-amber-400 mt-1 font-mono">{rp(data.pajak_total)}</p></div>
      <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-xs text-slate-400 font-semibold">Jumlah Catatan Pajak</p><p className="text-2xl font-black text-white mt-1">{data.pajak_detail.length}</p></div>
    </div>
    <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-x-auto">
      <h3 className="text-sm font-bold text-white border-b border-slate-800 pb-2.5 mb-2 flex items-center gap-2"><Receipt className="w-4 h-4 text-amber-400" /> Rincian Pajak</h3>
      <table className="w-full text-left text-xs">
        <thead><tr className="border-b border-slate-800 text-slate-400 font-semibold"><th className="py-2 px-3">Judul</th><th className="py-2 px-3">Jenis</th><th className="py-2 px-3">Dasar Pengenaan</th><th className="py-2 px-3">Tarif</th><th className="py-2 px-3">Nominal</th><th className="py-2 px-3">Status</th></tr></thead>
        <tbody className="divide-y divide-slate-800/60">
          {data.pajak_detail.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-500">Belum ada data pajak untuk periode ini.</td></tr>}
          {data.pajak_detail.map((p) => (
            <tr key={p.id} data-testid={`pajak-row-${p.id}`}>
              <td className="py-2.5 px-3 text-white font-semibold">{p.judul}</td>
              <td className="py-2.5 px-3 text-slate-300">{p.jenis}</td>
              <td className="py-2.5 px-3 font-mono text-slate-400">{rp(p.dasar_pengenaan)}</td>
              <td className="py-2.5 px-3 text-slate-300">{p.tarif_persen}%</td>
              <td className="py-2.5 px-3 font-mono font-bold text-amber-400">{rp(p.nominal)}</td>
              <td className="py-2.5 px-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${p.status === "SUDAH_SETOR" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{p.status === "SUDAH_SETOR" ? "SUDAH SETOR" : "BELUM SETOR"}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Neraca = ({ data, loading, periodeLabel }) => {
  if (loading || !data) return <div className="p-10 text-center text-slate-500 text-sm">Memuat neraca…</div>;
  const Section = ({ title, rows, total, totalLabel, accent }) => (
    <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
      <h3 className={`text-sm font-black border-b border-slate-800 pb-2.5 mb-2 ${accent}`}>{title}</h3>
      <table className="w-full text-left text-xs">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-800/50" data-testid={`neraca-${title.toLowerCase()}-row-${i}`}>
              <td className="py-2.5 px-3 text-slate-300">{r.label}</td>
              <td className="py-2.5 px-3 text-right font-mono font-bold text-white">{rp(r.value)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-slate-700">
            <td className={`py-2.5 px-3 font-black ${accent}`}>{totalLabel}</td>
            <td className={`py-2.5 px-3 text-right font-mono font-black text-base ${accent}`}>{rp(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-emerald-950/30 border border-emerald-500/40"><p className="text-xs text-slate-400 font-semibold">Total Aset</p><p className="text-2xl font-black text-emerald-400 mt-1 font-mono" data-testid="neraca-total-aset">{rp(data.total_aset)}</p></div>
        <div className="p-4 rounded-2xl bg-rose-950/30 border border-rose-500/40"><p className="text-xs text-slate-400 font-semibold">Total Kewajiban</p><p className="text-2xl font-black text-rose-400 mt-1 font-mono">{rp(data.total_kewajiban)}</p></div>
        <div className="p-4 rounded-2xl bg-cyan-950/30 border border-cyan-500/40"><p className="text-xs text-slate-400 font-semibold">Total Ekuitas</p><p className="text-2xl font-black text-cyan-400 mt-1 font-mono">{rp(data.total_ekuitas)}</p></div>
      </div>

      <div className={`p-3 rounded-xl border text-xs flex items-center gap-2 ${data.balanced ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-amber-500/10 border-amber-500/30 text-amber-300"}`} data-testid="neraca-balance-status">
        <Scale className="w-4 h-4 shrink-0" />
        <span>
          {data.balanced ? "SEIMBANG" : "PERHATIAN"} · Total Aset ({rp(data.total_aset)}) {data.balanced ? "=" : "≠"} Total Kewajiban + Ekuitas ({rp(data.total_kewajiban_ekuitas)}). Posisi per akhir {periodeLabel}.
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Aset" rows={data.aset_rows} total={data.total_aset} totalLabel="TOTAL ASET" accent="text-emerald-400" />
        <div className="space-y-6">
          <Section title="Kewajiban" rows={data.kewajiban_rows} total={data.total_kewajiban} totalLabel="TOTAL KEWAJIBAN" accent="text-rose-400" />
          <Section title="Ekuitas" rows={data.ekuitas_rows} total={data.total_ekuitas} totalLabel="TOTAL EKUITAS" accent="text-cyan-400" />
          <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-700 flex items-center justify-between">
            <span className="text-sm font-black text-white">TOTAL KEWAJIBAN + EKUITAS</span>
            <span className="text-base font-mono font-black text-cyan-400">{rp(data.total_kewajiban_ekuitas)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PerubahanModal = ({ data, loading, periodeLabel, tren }) => {
  if (loading || !data) return <div className="p-10 text-center text-slate-500 text-sm">Memuat laporan perubahan modal…</div>;
  const line = (label, value, op, strong) => (
    <div className={`flex items-center justify-between py-2.5 px-3 border-b border-slate-800/60 ${strong ? "bg-cyan-950/20" : ""}`}>
      <span className={`text-xs ${strong ? "font-black text-cyan-300" : "text-slate-300"}`}>{op ? <span className="text-slate-500 font-mono mr-1">{op}</span> : null}{label}</span>
      <span className={`font-mono ${strong ? "font-black text-cyan-300 text-base" : "font-bold text-white"} ${value < 0 ? "text-rose-400" : ""}`}>{rp(value)}</span>
    </div>
  );
  const trenRows = tren?.rows || [];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-4 rounded-2xl bg-slate-900/50 border border-slate-700"><p className="text-xs text-slate-400 font-semibold">Modal Awal</p><p className="text-xl font-black text-slate-200 mt-1 font-mono" data-testid="pmodal-awal">{rp(data.modal_awal)}</p></div>
        <div className="p-4 rounded-2xl bg-cyan-950/30 border border-cyan-500/40"><p className="text-xs text-slate-400 font-semibold">Modal Akhir</p><p className="text-xl font-black text-cyan-400 mt-1 font-mono" data-testid="pmodal-akhir">{rp(data.modal_akhir)}</p></div>
      </div>

      <div className="rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-hidden">
        <h3 className="text-sm font-black text-white px-3 py-2.5 border-b border-slate-800 flex items-center gap-2"><Coins className="w-4 h-4 text-amber-400" /> Ringkas Perubahan Modal — {periodeLabel}</h3>
        {line("Modal Awal (posisi akhir periode lalu)", data.modal_awal, "")}
        {line("Setoran Modal (periode ini)", data.setoran_modal, "+")}
        {line("Laba Bersih (periode ini)", data.laba_bersih, "+")}
        {line("Pengambilan Dividen / Prive (periode ini)", -data.prive, "−")}
        {line("Modal Akhir", data.modal_akhir, "=", true)}
      </div>

      {trenRows.length > 0 && (
        <div className="rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl p-4" data-testid="pmodal-tren">
          <h3 className="text-sm font-black text-white mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-400" /> Tren Modal & Laba (6 Bulan)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trenRows} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} tickFormatter={(v) => (v >= 1e6 ? (v / 1e6).toFixed(0) + "jt" : v)} width={44} />
              <Tooltip contentStyle={{ background: "#0e1628", border: "1px solid #334155", borderRadius: 12, fontSize: 12 }} formatter={(v) => rp(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="modal_akhir" name="Modal Akhir" stroke="#22d3ee" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="laba_bersih" name="Laba Bersih" stroke="#34d399" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-hidden">
        <h3 className="text-sm font-black text-white px-3 py-2.5 border-b border-slate-800 flex items-center gap-2"><Users className="w-4 h-4 text-amber-400" /> Rincian Modal per Mitra / Owner</h3>
        <div className="overflow-x-auto">
        <table className="w-full text-left text-xs min-w-[560px]">
          <thead className="bg-slate-900/60 text-slate-400">
            <tr><th className="py-2.5 px-3">Nama</th><th className="py-2.5 px-3">Tipe</th><th className="py-2.5 px-3 text-right">Modal Disetor</th><th className="py-2.5 px-3 text-right">Prive</th><th className="py-2.5 px-3 text-right">Modal Bersih</th><th className="py-2.5 px-3 text-center">% Sharing</th></tr>
          </thead>
          <tbody>
            {data.rincian_mitra.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-500">Belum ada setoran modal tercatat.</td></tr>}
            {data.rincian_mitra.map((r, i) => (
              <tr key={i} className="border-b border-slate-800/50" data-testid={`pmodal-mitra-row-${i}`}>
                <td className="py-2.5 px-3 text-slate-200 font-semibold">{r.nama}</td>
                <td className="py-2.5 px-3 text-slate-400">{r.tipe}</td>
                <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-300">{rp(r.modal_disetor)}</td>
                <td className="py-2.5 px-3 text-right font-mono text-rose-300">{r.prive > 0 ? rp(r.prive) : "-"}</td>
                <td className="py-2.5 px-3 text-right font-mono font-black text-white">{rp(r.modal_bersih)}</td>
                <td className="py-2.5 px-3 text-center font-mono text-amber-300">{r.prosentase_sharing == null ? "-" : `${Number(r.prosentase_sharing).toFixed(1)}%`}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-700 bg-slate-900/40">
              <td className="py-2.5 px-3 font-black text-white" colSpan={2}>TOTAL</td>
              <td className="py-2.5 px-3 text-right font-mono font-black text-emerald-400">{rp(data.total_modal_disetor)}</td>
              <td className="py-2.5 px-3 text-right font-mono font-black text-rose-400">{rp(data.total_prive || 0)}</td>
              <td className="py-2.5 px-3 text-right font-mono font-black text-white">{rp(data.total_modal_bersih || 0)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

const BagiHasil = ({ data }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div className="p-4 rounded-2xl bg-cyan-950/30 border border-cyan-500/40"><p className="text-xs text-slate-400 font-semibold">Sisa Profit (Dibagikan)</p><p className="text-2xl font-black text-cyan-400 mt-1 font-mono">{rp(Math.max(data.sisa_profit, 0))}</p></div>
      <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-xs text-slate-400 font-semibold">{data.scope_locked ? "Bagian Anda" : "Total Bagian Mitra"}</p><p className="text-2xl font-black text-purple-400 mt-1 font-mono">{rp(data.mitra_total)}</p></div>
      {!data.scope_locked && (
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-xs text-slate-400 font-semibold">Bagian Perusahaan</p><p className="text-2xl font-black text-emerald-400 mt-1 font-mono">{rp(data.bagian_perusahaan)}</p></div>
      )}
    </div>
    {data.sisa_profit <= 0 && <div className="p-3 bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-xl text-xs">Sisa profit periode ini ≤ 0, tidak ada laba yang dibagikan ke mitra.</div>}
    <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-x-auto">
      <h3 className="text-sm font-bold text-white border-b border-slate-800 pb-2.5 mb-2 flex items-center gap-2"><Users className="w-4 h-4 text-purple-400" /> Distribusi Bagi Hasil Mitra</h3>
      <table className="w-full text-left text-xs">
        <thead><tr className="border-b border-slate-800 text-slate-400 font-semibold"><th className="py-2 px-3">Mitra / Investor</th><th className="py-2 px-3">Modal</th><th className="py-2 px-3">Pelanggan</th><th className="py-2 px-3">Pendapatan Terkait</th><th className="py-2 px-3">% Sharing</th><th className="py-2 px-3 text-right">Bagian Laba</th></tr></thead>
        <tbody className="divide-y divide-slate-800/60">
          {data.mitra_sharing.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-500">Belum ada mitra/investor aktif. Tambahkan di Master Data → Mitra.</td></tr>}
          {data.mitra_sharing.map((m) => (
            <tr key={m.id} data-testid={`mitra-row-${m.id}`}>
              <td className="py-2.5 px-3 text-white font-semibold flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5 text-slate-500" /> {m.nama}</td>
              <td className="py-2.5 px-3 font-mono text-slate-400">{rp(m.jumlah_modal)}</td>
              <td className="py-2.5 px-3 text-slate-300">{m.jumlah_pelanggan}</td>
              <td className="py-2.5 px-3 font-mono text-slate-400">{rp(m.pendapatan_terkait)}</td>
              <td className="py-2.5 px-3 text-purple-300 font-bold">{m.prosentase_sharing}%</td>
              <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-400">{rp(m.bagian)}</td>
            </tr>
          ))}
        </tbody>
        {data.mitra_sharing.length > 0 && (
          <tfoot><tr className="border-t-2 border-slate-700"><td className="py-2.5 px-3 font-black text-white" colSpan={5}>TOTAL BAGIAN MITRA</td><td className="py-2.5 px-3 text-right font-mono font-black text-purple-400">{rp(data.mitra_total)}</td></tr></tfoot>
        )}
      </table>
    </div>
  </div>
);
